// Helpers for weapon-specific data coming from the backend `weapon` block.
// This centralizes labels and formatting so UI components stay lean.

import { TYPES } from './buildUtils';

const LANG_FR = 'fr';
const LANG_EN = 'en';
const LANGS = [LANG_FR, LANG_EN, 'de', 'es', 'pt'];

// "x à y" in French; each language joins a damage range its own way.
const RANGE_JOINER = { fr: 'à', en: 'to', de: 'bis', es: 'a', pt: 'a' };

const IMG_BASE = '/img';
export const ELEMENT_ICONS = {
    0: `${IMG_BASE}/effet_dn.png`,
    1: `${IMG_BASE}/effet_dt.png`,
    2: `${IMG_BASE}/effet_df.png`,
    3: `${IMG_BASE}/effet_de.png`,
    4: `${IMG_BASE}/effet_da.png`,
};

// Same elements, but for lifesteal ("vol de vie") lines, which read as damage
// plus a heal and get their own icon rather than reusing the damage one.
export const LIFESTEAL_ICONS = {
    0: `${IMG_BASE}/effet_vn.png`,
    1: `${IMG_BASE}/effet_vt.png`,
    2: `${IMG_BASE}/effet_vf.png`,
    3: `${IMG_BASE}/effet_ve.png`,
    4: `${IMG_BASE}/effet_va.png`,
};

// Lifesteal ("vol de vie") effect ids. A set and not an id -> icon table,
// because the element an id was minted with is not the element the line deals:
// forgemaging moves a neutral steal onto an element by rewriting `element_id`
// and leaving `effect_id` alone (`weaponMaging.js`). The icon follows the
// element the line carries now; pinned to the id, a maged steal kept the
// neutral heart above an elemental number.
export const LIFESTEAL_EFFECT_IDS = new Set([82, 89, 95, 92, 94, 91, 93]);

// Heal ("soins") effect ids — mirrors the backend's `HEAL_EFFECT_IDS`
// (core/services/dofus_api/constants.py): standard, % max HP, then Fire, Water,
// Air, Earth, Neutral and best-element. A set for the same reason lifesteal is
// one: the icon follows the element the line carries now, not the one its id
// was minted with.
export const HEAL_EFFECT_IDS = new Set([108, 1109, 2998, 2999, 3000, 3001, 3002]);

// Specific icons for notable weapon effect IDs.
export const EFFECT_ID_ICONS = {
    2822: `${IMG_BASE}/effet_dme.png`, // dommages du meilleur élément
    2828: `${IMG_BASE}/effet_vme.png`, // vol du meilleur élément
    6: `${IMG_BASE}/effet_att.png`, // attire
    5: `${IMG_BASE}/effet_pou.png`, // repousse
    101: `${IMG_BASE}/effet_minus.png`, // -PA
    127: `${IMG_BASE}/effet_minus.png`, // -PM
    77: `${IMG_BASE}/effet_minus.png`, // other AP/MP loss
};

// Element metadata keyed by Dofus element_id
export const ELEMENTS = {
    0: {
        key: 'neutral',
        icon: ELEMENT_ICONS[0],
        label: {
            fr: 'Neutre',
            en: 'Neutral',
            de: 'Neutral',
            es: 'Neutral',
            pt: 'Neutro',
        },
    },
    1: {
        key: 'earth',
        icon: ELEMENT_ICONS[1],
        label: {
            fr: 'Terre',
            en: 'Earth',
            de: 'Erde',
            es: 'Tierra',
            pt: 'Terra',
        },
    },
    2: {
        key: 'fire',
        icon: ELEMENT_ICONS[2],
        label: {
            fr: 'Feu',
            en: 'Fire',
            de: 'Feuer',
            es: 'Fuego',
            pt: 'Fogo',
        },
    },
    3: {
        key: 'water',
        icon: ELEMENT_ICONS[3],
        label: {
            fr: 'Eau',
            en: 'Water',
            de: 'Wasser',
            es: 'Agua',
            pt: 'Água',
        },
    },
    4: {
        key: 'air',
        icon: ELEMENT_ICONS[4],
        label: {
            fr: 'Air',
            en: 'Air',
            de: 'Luft',
            es: 'Aire',
            pt: 'Ar',
        },
    },
    5: {
        key: 'push',
        icon: null,
        label: {
            fr: 'Poussée',
            en: 'Pushback',
            de: 'Schubs',
            es: 'Empuje',
            pt: 'Empurrão',
        },
    },
};

const DEFAULT_ELEMENT = {
    key: 'unknown',
    icon: null,
    label: {
        fr: 'Élément',
        en: 'Element',
        de: 'Element',
        es: 'Elemento',
        pt: 'Elemento',
    },
};

const TXT_META = {
    ap: {
        fr: 'PA',
        en: 'AP',
        de: 'AP',
        es: 'PA',
        pt: 'PA',
    },
    range: {
        fr: 'PO',
        en: 'Range',
        de: 'RW',
        es: 'AL',
        pt: 'AL',
    },
    critChance: {
        fr: 'CC',
        en: 'crit',
        de: 'KT',
        es: 'GC',
        pt: 'GC',
    },
    critBonus: {
        fr: 'bonus crit.',
        en: 'crit bonus',
        de: 'Krit-Bonus',
        es: 'bonus crít.',
        pt: 'bônus crít.',
    },
    castsPerTurn: {
        fr: 'lancer par tour',
        en: 'cast per turn',
        de: 'Zauber pro Zug',
        es: 'lanzamiento por turno',
        pt: 'lançamento por turno',
    },
    castsPerTurnPlural: {
        fr: 'lancers par tour',
        en: 'casts per turn',
        de: 'Zauber pro Zug',
        es: 'lanzamientos por turno',
        pt: 'lançamentos por turno',
    },
    castLine: {
        fr: 'En ligne',
        en: 'Linear',
        de: 'In Linie',
        es: 'En línea',
        pt: 'Em linha',
    },
    castDiagonal: {
        fr: 'En diagonale',
        en: 'Diagonal',
        de: 'Diagonal',
        es: 'En diagonal',
        pt: 'Na diagonal',
    },
    twoHanded: {
        fr: 'Deux mains',
        en: 'Two-handed',
        de: 'Zweihändig',
        es: 'A dos manos',
        pt: 'Duas mãos',
    },
};

const getLang = (lang) => (LANGS.includes(lang) ? lang : LANG_FR);

/** Item type label (e.g. Arc / Bow) from `item.type` or `item_type_id`. */
export const getWeaponTypeLabel = (weaponItem, lang) => {
    if (!weaponItem) return null;
    const l = getLang(lang);
    const n = weaponItem.type?.name;
    if (n && typeof n === 'object') return n[l] || n[LANG_EN] || n[LANG_FR] || null;
    const tid = weaponItem.item_type_id;
    if (tid == null) return null;
    const row = TYPES.find((t) => t.id === tid);
    return row?.name?.[l] || row?.name?.[LANG_EN] || row?.name?.[LANG_FR] || null;
};

/** "1 lancer par tour" / "2 casts per turn" — same wording as `buildWeaponMetaRows`. */
export const formatWeaponCastsPerTurnPart = (weapon, lang) => {
    const mc = Number(weapon?.max_cast);
    if (!Number.isFinite(mc) || mc <= 0) return null;
    const currentLang = getLang(lang);
    const label = mc > 1 ? TXT_META.castsPerTurnPlural[currentLang] : TXT_META.castsPerTurn[currentLang];
    return `${mc} ${label}`;
};

/** Spell-card style subtitle: "Arc · 1 lancer par tour" (same separator as spell detail footer). */
export const buildWeaponCardDescription = (weaponItem, weapon, lang) => {
    const typePart = getWeaponTypeLabel(weaponItem, lang);
    const castsPart = weapon ? formatWeaponCastsPerTurnPart(weapon, lang) : null;
    const sep = ' · ';
    if (typePart && castsPart) return `${typePart}${sep}${castsPart}`;
    return [typePart, castsPart].filter(Boolean).join(sep);
};

const HEAL_ICONS = {
    fire: `${IMG_BASE}/stat_soin_feu.png`,
    water: `${IMG_BASE}/stat_soin_eau.png`,
    earth: `${IMG_BASE}/stat_soin_terre.png`,
    air: `${IMG_BASE}/stat_soin_air.png`,
    neutral: `${IMG_BASE}/stat_soin_generique.png`,
    heal_total: `${IMG_BASE}/stat_soin_generique.png`,
};

const ELEMENT_KEY_ICON_IDS = { neutral: 0, earth: 1, fire: 2, water: 3, air: 4 };

/** Icon path for spell line. kind: 'damage' | 'lifesteal' | 'heal', el: element key. */
export const getSpellElementIcon = (elKey, kind = 'damage') => {
    if (kind === 'heal') return HEAL_ICONS[elKey] || HEAL_ICONS.heal_total;
    const elementId = ELEMENT_KEY_ICON_IDS[elKey];
    if (elementId != null) {
        return kind === 'lifesteal' ? LIFESTEAL_ICONS[elementId] : ELEMENT_ICONS[elementId];
    }
    if (elKey === 'push') return EFFECT_ID_ICONS[5];
    // Best element is only resolved at cast time, so it has its own artwork
    // rather than any one element's.
    if (elKey === 'best_element') return EFFECT_ID_ICONS[kind === 'lifesteal' ? 2828 : 2822];
    return null;
};

// ── Damage colours ───────────────────────────────────────────────────────────
// A calculated damage number is tinted by the element it lands in. The values
// are tokens (`--damage-*` in tokens.css) so each theme states its own; what
// lives here is which key gets which, and the rule for a number that covers
// more than one.
const HEAL_KEY = 'heal';
const DAMAGE_COLOR_VARS = {
    neutral: 'var(--damage-neutral)',
    earth: 'var(--damage-earth)',
    fire: 'var(--damage-fire)',
    water: 'var(--damage-water)',
    air: 'var(--damage-air)',
    push: 'var(--damage-push)',
    // Not an element, and the one key that ignores the element it carries: a
    // heal is pink whether it is Fire or Water, because what the reader needs
    // from the number is that it gives health back rather than takes it.
    [HEAL_KEY]: 'var(--damage-heal)',
};
const DAMAGE_COLOR_MULTI = 'var(--damage-multi)';
// The element a best-element line deals is only settled at cast time, so one of
// them on its own already makes the number multi-element.
const MULTI_ELEMENT_KEYS = new Set(['best_element']);
// Every spelling a heal arrives under — `heal_total` is a spell's summed heal.
const HEAL_KEYS = new Set([HEAL_KEY, 'heal_total']);
// The custom property the colour is published as. Each consuming stylesheet
// falls back to its own colour, so a number with nothing to name — an effect
// with no element at all — is left exactly as it was.
const DAMAGE_COLOR_PROP = '--damage-color';

/**
 * The style carrying the colour a calculated number should wear, for the
 * elements it covers. Two or more of them — a weapon's total, a spell row
 * merged from several lines — is the multi colour rather than either part's;
 * pushback counts as an element for that, since a hit that pushes and burns is
 * two, and so does a heal, since a row that both heals and burns is neither.
 *
 * `undefined` when nothing in `keys` is something we colour, which leaves the
 * number to its own stylesheet.
 */
export const damageColorStyle = (elementKeys) => {
    const keys = new Set();
    for (const raw of elementKeys || []) {
        if (!raw) continue;
        if (MULTI_ELEMENT_KEYS.has(raw)) return { [DAMAGE_COLOR_PROP]: DAMAGE_COLOR_MULTI };
        const key = HEAL_KEYS.has(raw) ? HEAL_KEY : raw;
        if (DAMAGE_COLOR_VARS[key]) keys.add(key);
    }
    if (!keys.size) return undefined;
    if (keys.size > 1) return { [DAMAGE_COLOR_PROP]: DAMAGE_COLOR_MULTI };
    return { [DAMAGE_COLOR_PROP]: DAMAGE_COLOR_VARS[[...keys][0]] };
};

/**
 * Same, from the rendered lines behind a number. A heal line is a heal whatever
 * element it names, so it is keyed off `kind` and not off `el`.
 *
 * Callers pricing a damage *total* pass only the lines that total sums — the
 * weapon block leaves its heals out of `damageRange`, and a total that showed
 * them would be a different number than the one it colours.
 */
export const damageLinesColorStyle = (lines) => damageColorStyle(
    (lines || []).filter(Boolean).map((line) => (line.kind === HEAL_KEY ? HEAL_KEY : line.el))
);

/** The icon a rendered spell/weapon line shows, from its own kind and lifesteal flag. */
export const getSpellLineIcon = (line) => {
    if (line?.kind === 'heal') return getSpellElementIcon(line?.el, 'heal');
    return getSpellElementIcon(line?.el, line?.isLifesteal ? 'lifesteal' : 'damage');
};

export const getElementMeta = (elementId) => {
    if (elementId === null || elementId === undefined) {
        return DEFAULT_ELEMENT;
    }
    const meta = ELEMENTS[elementId];
    if (meta) return meta;
    return DEFAULT_ELEMENT;
};

const getWeaponEffects = (weapon) => {
    if (!weapon || !Array.isArray(weapon.effects)) {
        return [];
    }

    return weapon.effects.filter((effect) => !!effect);
};

export const hasWeaponDamages = (weapon) => {
    return getWeaponEffects(weapon).length > 0;
};

const resolveRange = (min, max) => {
    const safeMin = typeof min === 'number' ? min : 0;
    if (typeof max !== 'number' || max <= 0) {
        return { min: safeMin, max: safeMin, hasRange: false };
    }
    if (max === safeMin) {
        return { min: safeMin, max: safeMin, hasRange: false };
    }
    return { min: safeMin, max, hasRange: true };
};

const formatRangeText = (min, max, hasRange, lang) => {
    if (!hasRange) {
        return `${min}`;
    }
    const currentLang = getLang(lang);
    return `${min} ${RANGE_JOINER[currentLang] || RANGE_JOINER.en} ${max}`;
};

const expandEffectDescription = (effect, lang) => {
    const currentLang = getLang(lang);
    const description =
        (effect.desc && (effect.desc[currentLang] || effect.desc[LANG_EN] || effect.desc[LANG_FR])) ||
        '';

    const { min, max, hasRange } = resolveRange(effect.min, effect.max);
    const resolvedMax = hasRange ? max : min;

    const rangeText = formatRangeText(min, resolvedMax, hasRange, currentLang);

    let text = description;

    // Patterns like "#1{{~1~2 à }}#2 ..."
    text = text.replace(/#1\{\{[^}]*\}\}#2/g, () => rangeText);

    // Simple #1 / #2 placeholders
    text = text.replace(/#1/g, `${min}`);
    text = text.replace(/#2/g, `${resolvedMax}`);

    // Drop any remaining macro blocks like "{{~ps}}" used for pluralization.
    text = text.replace(/\{\{[^}]*\}\}/g, '');

    // Clean up duplicated minus signs that may appear when templates already include "-"
    // and min/max are negative (e.g. "--1PA" -> "-1PA").
    text = text.replace(/--\s*(\d+)/g, '-$1');

    return text.trim();
};

/** "329 à 461", or "329" when both ends agree — the joiner every weapon line uses. */
export const formatWeaponRange = (min, max, lang = LANG_FR) => {
    const range = resolveRange(min, max);
    return formatRangeText(range.min, range.hasRange ? range.max : range.min, range.hasRange, getLang(lang));
};

const getEffectIcon = (effect) => {
    if (!effect) {
        return `${IMG_BASE}/stat_do.png`;
    }

    // A steal line reads as its element before it reads as an id, and that
    // element can have been maged, so it is resolved ahead of the id table.
    if (LIFESTEAL_EFFECT_IDS.has(Number(effect.effect_id))) {
        return LIFESTEAL_ICONS[Number(effect.element_id)] || LIFESTEAL_ICONS[0];
    }

    // Same for a heal. Without this a "1 à 100 soins Feu" line fell through to
    // the element table and wore the Fire *damage* icon, which is the one thing
    // the row must not say.
    if (HEAL_EFFECT_IDS.has(Number(effect.effect_id))) {
        const elKey = ELEMENTS[Number(effect.element_id)]?.key;
        return HEAL_ICONS[elKey] || HEAL_ICONS.heal_total;
    }

    // First, prefer explicit mappings by effect_id when available.
    if (typeof effect.effect_id === 'number' && EFFECT_ID_ICONS[effect.effect_id]) {
        return EFFECT_ID_ICONS[effect.effect_id];
    }

    // Then fall back to element-based icons when element_id is known.
    if (typeof effect.element_id === 'number' && ELEMENT_ICONS[effect.element_id]) {
        return ELEMENT_ICONS[effect.element_id];
    }

    // Generic fallback icon for other weapon effects.
    return `${IMG_BASE}/stat_do.png`;
};

/**
 * The damage lines of a weapon, ready to render.
 *
 * Rows are the dice, as printed on the item. A caller that also holds computed
 * lines from `calcWeaponHitLines` pairs them up by `index`.
 */
export const buildWeaponDamageRows = (weapon, lang = LANG_FR) => {
    const effects = getWeaponEffects(weapon);
    if (!effects.length) return [];

    const currentLang = getLang(lang);

    return effects.map((effect, index) => {
        const elementMeta = getElementMeta(effect.element_id);

        return {
            key: `effect-${index}-${effect.effect_id || 'unknown'}`,
            // Position in `weapon.effects`, which is what a computed line names.
            index,
            elementKey: elementMeta.key,
            icon: getEffectIcon(effect),
            text: expandEffectDescription(effect, currentLang),
        };
    });
};

export const buildWeaponMetaRows = (weapon, lang = LANG_FR) => {
    if (!weapon) return [];

    const currentLang = getLang(lang);
    const rows = [];

    if (typeof weapon.ap === 'number') {
        rows.push({
            key: 'ap',
            text: `${weapon.ap} ${TXT_META.ap[currentLang]}`,
        });
    }

    if (typeof weapon.min_range === 'number' && typeof weapon.max_range === 'number') {
        const rangeText =
            currentLang === LANG_FR
                ? `${weapon.min_range} - ${weapon.max_range} ${TXT_META.range[currentLang]}`
                : `${weapon.min_range} - ${weapon.max_range} ${TXT_META.range[currentLang]}`;
        rows.push({
            key: 'range',
            text: rangeText,
        });
    }

    if (typeof weapon.crit_chance === 'number') {
        let critText = `${weapon.crit_chance}% ${TXT_META.critChance[currentLang]}`;
        if (typeof weapon.crit_bonus === 'number' && weapon.crit_bonus !== 0) {
            critText += ` (+${weapon.crit_bonus})`;
        }
        rows.push({
            key: 'crit',
            text: critText,
        });
    }

    if (typeof weapon.max_cast === 'number' && weapon.max_cast > 0) {
        const label =
            weapon.max_cast > 1
                ? TXT_META.castsPerTurnPlural[currentLang]
                : TXT_META.castsPerTurn[currentLang];
        rows.push({
            key: 'casts',
            text: `${weapon.max_cast} ${label}`,
        });
    }

    if (weapon.cast_line) {
        rows.push({
            key: 'cast_line',
            text: TXT_META.castLine[currentLang],
        });
    }

    if (weapon.cast_diag) {
        rows.push({
            key: 'cast_diag',
            text: TXT_META.castDiagonal[currentLang],
        });
    }

    if (weapon.two_handed) {
        rows.push({
            key: 'two_handed',
            text: TXT_META.twoHanded[currentLang],
        });
    }

    return rows;
};

